/**
 * Spec 072 (#1173) — a recovery provider's insurance disclaimer, as the admin previews it.
 *
 * The customer's note is built by orders (`LegInsuranceNoteResolver`) from the leg's frozen snapshot;
 * these helpers only show the admin what that will read. They follow the server's rules so the
 * preview cannot promise a note the customer will not get: shown only with the toggle on, an amount
 * above 0 and a text, `{amount}` filled as "350,000" (grouped, no decimals, Western digits).
 */

/** Requirements §7 — filled in when the toggle is first switched on with both texts empty. */
export const DEFAULT_DISCLAIMER_EN =
  'Your car is driven by a Zofeur driver and insured up to AED {amount} while they have it. '
  + 'Cover applies only if the driver is at fault, per the police report. Zofeur terms apply.';
export const DEFAULT_DISCLAIMER_AR =
  'يقود سيارتك سائق من Zofeur، وهي مؤمَّنة بما يصل إلى {amount} درهم أثناء وجودها معه. '
  + 'تسري التغطية فقط إذا كان السائق مخطئًا وفق تقرير الشرطة. تطبَّق شروط Zofeur.';

export const AMOUNT_PLACEHOLDER = '{amount}';
export const DISCLAIMER_MAX_LENGTH = 500;
export const MAX_INSURED_AMOUNT = 10_000_000;

/** Server wording (§6), so a client-side check reads exactly like the 422 it pre-empts. */
export const INSURANCE_MESSAGES = {
  amountRange: 'Enter an insured amount between 0 and 10,000,000 AED',
  amountRequired: 'Enter the insured amount before showing the disclaimer',
  textsRequired: 'Enter the disclaimer in English and Arabic',
  textLength: 'Keep the disclaimer under 500 characters',
  phoneRequired: "Enter the driver's phone for this provider",
} as const;

/** 350000 → "350,000". */
export function formatInsuredAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount);
}

/** The template with `{amount}` filled; left as written when there is no amount yet. */
export function fillDisclaimer(template: string, amount: number | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return template;
  return template.split(AMOUNT_PLACEHOLDER).join(formatInsuredAmount(amount));
}

/**
 * The text the customer will see for a provider (or a leg's snapshot), or null when no note is
 * shown: the same eligibility the server applies.
 */
export function customerDisclaimer(
  show: boolean | null | undefined,
  amount: number | null | undefined,
  template: string | null | undefined,
): string | null {
  if (!show || amount === null || amount === undefined || !(amount > 0)) return null;
  if (!template || !template.trim()) return null;
  return fillDisclaimer(template, amount);
}

/** The registry's Insurance column: "Insured AED 350,000 · shown" / "· hidden", or null for no cover. */
export function insuranceBadge(amount: number | null | undefined, show: boolean | null | undefined): string | null {
  if (amount === null || amount === undefined || !(amount > 0)) return null;
  return `Insured AED ${formatInsuredAmount(amount)} · ${show ? 'shown' : 'hidden'}`;
}
